import { useTranslate, useWarnAboutChange } from '@refinedev/core';
import { Alert, App as AntdApp, Button, Drawer, Grid } from 'antd';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { SubmitFooter } from '../SubmitFooter';

/**
 * The ONE drawer shell of the tool sections (design PORTAL-SECTIONS-2026-10-22 §1.3). Save is never disabled for a missing field: pressing
 * it validates, and a problem becomes `error` — a sentence shown ABOVE the footer buttons until the caller clears it on the next edit
 * (the AssignHomeworkDrawer's Q-mt-2 A rule). The caller closes the drawer after a successful save (bypassing the guard), toasts, refetches.
 *
 * The unsaved-change guard: while `open && dirty` the app's navigation warning is armed, and closing (×, mask, Esc, Cancel) asks first;
 * a second Esc while that confirm is open is ignored (no stacked confirms) and the confirm's own Esc is "Keep editing".
 */
export type SectionDrawerProps = {
  open: boolean;
  title: string;
  /** A div wrapping the BODY carries it; the footer is `${testId}-save` / `${testId}-cancel`, the error `${testId}-error`. */
  testId: string;
  /** Keep an existing submit testid where specs use it. */
  saveTestId?: string;
  width?: 'default' | 'wide';
  /** READ-ONLY facts the therapist needs while filling the form — the top of the body. */
  context?: ReactNode;
  children: ReactNode;
  saveLabel: string;
  saving: boolean;
  danger?: boolean;
  /** LASTING: cleared by the caller on the next edit, never on a timer. */
  error: string | null;
  /** The form differs from what it opened with (a deep compare), not "touched". */
  dirty: boolean;
  onSave: () => void;
  /** Called after the dirty guard; the caller resets its draft. */
  onClose: () => void;
};

export const SectionDrawer = ({
  open,
  title,
  testId,
  saveTestId,
  width = 'default',
  context,
  children,
  saveLabel,
  saving,
  danger,
  error,
  dirty,
  onSave,
  onClose,
}: SectionDrawerProps) => {
  const t = useTranslate();
  const { modal } = AntdApp.useApp();
  const screens = Grid.useBreakpoint();
  const { setWarnWhen } = useWarnAboutChange();
  const confirmingRef = useRef(false);

  const armed = open && dirty;
  useEffect(() => {
    setWarnWhen(armed);
    return () => setWarnWhen(false);
  }, [armed, setWarnWhen]);

  const requestClose = useCallback(() => {
    if (!dirty) {
      onClose();
      return;
    }
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    modal.confirm({
      title: t('pages.toolSection.drawer.discardTitle'),
      content: t('pages.toolSection.drawer.discardBody'),
      okText: t('pages.toolSection.drawer.discard'),
      okButtonProps: { danger: true },
      cancelText: t('pages.toolSection.drawer.keepEditing'),
      onOk: () => {
        confirmingRef.current = false;
        setWarnWhen(false);
        onClose();
      },
      onCancel: () => {
        confirmingRef.current = false;
      },
    });
  }, [dirty, modal, onClose, setWarnWhen, t]);

  // below ~600 px the drawer takes the whole screen
  const phone = screens.sm === false;
  const drawerWidth = phone ? '100vw' : width === 'wide' ? 760 : 560;

  return (
    <Drawer
      open={open}
      title={title}
      width={drawerWidth}
      destroyOnClose
      maskClosable
      onClose={requestClose}
      footer={
        <SubmitFooter
          errors={
            error ? (
              <Alert
                type="error"
                showIcon
                message={error}
                data-testid={`${testId}-error`}
              />
            ) : null
          }
        >
          <Button onClick={requestClose} data-testid={`${testId}-cancel`}>
            {t('pages.toolSection.drawer.cancel')}
          </Button>
          <Button
            type="primary"
            danger={danger}
            loading={saving}
            onClick={onSave}
            data-testid={saveTestId ?? `${testId}-save`}
          >
            {saveLabel}
          </Button>
        </SubmitFooter>
      }
    >
      <div data-testid={testId}>
        {context ? (
          <div style={{ marginBottom: 16 }} data-testid={`${testId}-context`}>
            {context}
          </div>
        ) : null}
        {children}
      </div>
    </Drawer>
  );
};
